import React, { useState, useEffect } from "react";
import { Zap, Timer, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import arthereumProvider from "../../backend/ArthereumProvider";
import { actions } from "../redux";
import useThemeHook from "../hooks/useThemeHook";
import { FLASH_BOOST_COST } from "../utils/rewardTables";

const FLASH_DURATION_MS = 10 * 60 * 1000; // 10 minutes availability window
const PI_COST = FLASH_BOOST_COST;

const FlashBoostCard: React.FC = () => {
  const theme = useThemeHook();
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state.actualUser);
  const user = userData?.user;

  const [isAvailable, setIsAvailable] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [loading, setLoading] = useState(false);

  // Simulate random flash availability (once per session)
  useEffect(() => {
    const flashKey = `flash_offered_${new Date().toISOString().split("T")[0]}`;
    if (localStorage.getItem(flashKey)) return;

    const randomDelay = Math.random() * 30000 + 10000; // 10-40 seconds after load
    const offerTimeout = setTimeout(() => {
      setIsAvailable(true);
      setRemainingSeconds(600); // 10 minutes
      localStorage.setItem(flashKey, "true");

      // Auto-expire after 10 minutes
      const expireTimeout = setTimeout(() => setIsAvailable(false), FLASH_DURATION_MS);
      return () => clearTimeout(expireTimeout);
    }, randomDelay);

    return () => clearTimeout(offerTimeout);
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!isAvailable || remainingSeconds <= 0) return;
    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          setIsAvailable(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isAvailable, remainingSeconds]);

  const handleActivate = async () => {
    if (!user?.id) return;
    if (user.picoinBalance < PI_COST) {
      alert("Insufficient Pi balance!");
      return;
    }

    try {
      setLoading(true);
      await arthereumProvider.activateFlashBoost(user.id);
      // Update Redux locally instead of re-fetching from Firestore
      dispatch(actions.setActualUser({
        ...userData,
        user: {
          ...user,
          picoinBalance: (user.picoinBalance || 0) - PI_COST,
          flashBoostExpiry: { seconds: Math.floor(Date.now() / 1000) + 3600 },
        },
      }));
      setIsAvailable(false);
      alert("⚡ FLASH BOOST ACTIVATED! Your next grants will be 10x for 1 hour!");
    } catch (error: any) {
      alert(error.message || "Activation failed");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (!isAvailable) return null;

  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      className={`mx-4 mt-6 p-5 rounded-3xl border-2 overflow-hidden relative ${
        theme === "dark" ? "bg-amber-900/20 border-amber-500/50" : "bg-amber-50 border-amber-200"
      } shadow-xl shadow-amber-500/20`}
    >
      {/* Pulsing background */}
      <div className="absolute inset-0 bg-gradient-to-r from-amber-400/5 to-orange-500/5 animate-pulse" />

      <div className="flex items-start gap-4 relative z-10">
        <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-3 rounded-2xl shadow-lg shadow-amber-500/30 animate-bounce">
          <Zap size={24} className="text-white fill-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={`text-sm font-black uppercase tracking-tight ${theme === "dark" ? "text-white" : "text-amber-900"}`}>
              ⚡ FLASH BOOST AVAILABLE!
            </h3>
          </div>
          <p className={`text-[11px] font-bold leading-tight ${theme === "dark" ? "text-slate-300" : "text-amber-700"}`}>
            Get <span className="underline decoration-2">10X MINING POWER</span> for 1 hour! This offer expires soon.
          </p>

          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={handleActivate}
              disabled={loading}
              className="flex-1 py-3 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white rounded-xl font-black text-xs flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 active:scale-95 transition-all"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>ACTIVATE FOR {PI_COST} PI</>
              )}
              <ChevronRight size={14} />
            </button>
            <div className={`flex items-center gap-1.5 px-3 py-2 rounded-xl ${theme === "dark" ? "bg-slate-800" : "bg-white"} border border-amber-200`}>
              <Timer size={14} className="text-amber-500" />
              <span className="text-xs font-black text-amber-600">{formatTime(remainingSeconds)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Large background icon */}
      <Zap size={100} className="absolute -bottom-6 -right-6 text-amber-500/10 rotate-12 fill-amber-500/5" />
    </motion.div>
  );
};

export default FlashBoostCard;
